import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { uploadManualStock, clearUploadResult } from '../store/slices/inventorySlice';
import { StockEntry } from '../types/inventory';
import { Package, Plus, Upload, Save, AlertTriangle, CheckCircle, X } from 'lucide-react';
import FileUpload from '../components/inventory/FileUpload';
import LocationSuggestionComponent from '../components/inventory/LocationSuggestion';

const StockEntryPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { uploadLoading, uploadResult } = useAppSelector((state) => state.inventory);

  const [activeTab, setActiveTab] = useState<'manual' | 'upload' | 'suggest'>('manual');
  const [stockEntries, setStockEntries] = useState<StockEntry[]>([
    {
      date: new Date().toISOString().split('T')[0],
      item_code: '',
      color: '',
      size: '',
      quantity: 1,
      gatepass_number: ''
    }
  ]);

  const handleAddEntry = () => {
    setStockEntries([
      ...stockEntries,
      {
        date: new Date().toISOString().split('T')[0],
        item_code: '',
        color: '',
        size: '',
        quantity: 1,
        gatepass_number: ''
      }
    ]);
  };

  const handleRemoveEntry = (index: number) => {
    if (stockEntries.length > 1) {
      setStockEntries(stockEntries.filter((_, i) => i !== index));
    }
  };

  const handleEntryChange = (index: number, field: keyof StockEntry, value: string | number) => {
    const updatedEntries = [...stockEntries];
    updatedEntries[index] = {
      ...updatedEntries[index],
      [field]: value
    };
    setStockEntries(updatedEntries);
  };

  const handleSaveEntries = async () => {
    // Validate entries
    const validEntries = stockEntries.filter(entry => 
      entry.item_code.trim() && 
      entry.color.trim() && 
      entry.size.trim() && 
      entry.quantity > 0 && 
      entry.gatepass_number.trim()
    );

    if (validEntries.length === 0) {
      alert('Please fill in at least one complete entry');
      return;
    }

    // Convert to CSV format and upload
    const csvHeader = 'DATE,ITEM CODE,COLOR,SIZE,QTY,GATEPASS NUMBER\n';
    const csvData = validEntries.map(entry => 
      `${entry.date},${entry.item_code},${entry.color},${entry.size},${entry.quantity},${entry.gatepass_number}`
    ).join('\n');
    
    const csvContent = csvHeader + csvData;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const file = new File([blob], `manual_stock_${Date.now()}.csv`, { type: 'text/csv' });

    try {
      await dispatch(uploadManualStock(file)).unwrap();
      // Clear entries after successful upload
      setStockEntries([{
        date: new Date().toISOString().split('T')[0],
        item_code: '',
        color: '',
        size: '',
        quantity: 1,
        gatepass_number: ''
      }]);
    } catch (error) {
      console.error('Failed to save entries:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Entry</h1>
          <p className="text-gray-600">Record new stock arrivals manually or upload from file</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('manual')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'manual'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Package className="h-4 w-4 inline mr-2" />
            Manual Entry
          </button>

          <button
            onClick={() => setActiveTab('upload')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'upload'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Upload className="h-4 w-4 inline mr-2" />
            File Upload
          </button>

          <button
            onClick={() => setActiveTab('suggest')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'suggest'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Package className="h-4 w-4 inline mr-2" />
            Location Suggestion
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow">
        {activeTab === 'manual' && (
          <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Manual Stock Entry</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleAddEntry}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Entry
                </button>
                <button
                  onClick={handleSaveEntries}
                  disabled={uploadLoading}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {uploadLoading ? 'Saving...' : 'Save Entries'}
                </button>
              </div>
            </div>

            {/* Stock Entries Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item Code *
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Color *
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size *
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity *
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gatepass Number *
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stockEntries.map((entry, index) => (
                    <tr key={index}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="date"
                          value={entry.date}
                          onChange={(e) => handleEntryChange(index, 'date', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="text"
                          placeholder="e.g. GFW23669"
                          value={entry.item_code}
                          onChange={(e) => handleEntryChange(index, 'item_code', e.target.value.toUpperCase())}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <select
                          value={entry.color}
                          onChange={(e) => handleEntryChange(index, 'color', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="">Select Color</option>
                          <option value="Black">Black</option>
                          <option value="White">White</option>
                          <option value="Blue">Blue</option>
                          <option value="Red">Red</option>
                          <option value="Green">Green</option>
                          <option value="Beige">Beige</option>
                          <option value="Khaki">Khaki</option>
                          <option value="Wine">Wine</option>
                          <option value="Navy">Navy</option>
                          <option value="Grey">Grey</option>
                        </select>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <select
                          value={entry.size}
                          onChange={(e) => handleEntryChange(index, 'size', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="">Select Size</option>
                          <option value="UK 6">UK 6</option>
                          <option value="UK 8">UK 8</option>
                          <option value="UK 10">UK 10</option>
                          <option value="UK 12">UK 12</option>
                          <option value="UK 14">UK 14</option>
                          <option value="UK 16">UK 16</option>
                        </select>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          min="1"
                          value={entry.quantity}
                          onChange={(e) => handleEntryChange(index, 'quantity', parseInt(e.target.value) || 1)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="text"
                          placeholder="e.g. GP001"
                          value={entry.gatepass_number}
                          onChange={(e) => handleEntryChange(index, 'gatepass_number', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {stockEntries.length > 1 && (
                          <button
                            onClick={() => handleRemoveEntry(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Upload Result */}
            {uploadResult && (
              <div className={`rounded-lg p-4 ${uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-start">
                  {uploadResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                  )}
                  
                  <div className="ml-3 flex-1">
                    <h3 className={`text-sm font-medium ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {uploadResult.success ? 'Entries Saved Successfully' : 'Save Failed'}
                    </h3>
                    
                    <p className={`text-sm mt-1 ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {uploadResult.message}
                    </p>
                    
                    {uploadResult.success && (
                      <div className="mt-3 space-y-1 text-sm text-green-700">
                        <p>• Processed records: {uploadResult.processed_records}</p>
                        {uploadResult.pending_approval && <p>• Pending approval: {uploadResult.pending_approval}</p>}
                        {uploadResult.auto_approved && <p>• Auto approved: {uploadResult.auto_approved}</p>}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => dispatch(clearUploadResult())}
                    className="ml-3 text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-blue-500" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Entry Instructions</h3>
                  <div className="text-sm text-blue-700 mt-1 space-y-1">
                    <p>• All fields marked with * are required</p>
                    <p>• Item codes should match existing styles (e.g., GFW23669)</p>
                    <p>• Unknown styles will be marked for manager approval</p>
                    <p>• Gatepass numbers help track stock receipts</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'upload' && (
          <div className="p-6">
            <FileUpload />
          </div>
        )}

        {activeTab === 'suggest' && (
          <div className="p-6">
            <LocationSuggestionComponent />
          </div>
        )}
      </div>
    </div>
  );
};

export default StockEntryPage;
